import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { RadioInput, TextArea } from "@jhool-io/fe-components";
import useToast from "../../../../hooks/useToast";
import InfoIcon from "../../../../icons/Info";
import XCircleIcon from "../../../../icons/XCircle";
import {
    useResolveNoteChange,
    useUpdateChangeRequestOutcome,
} from "../../../../hooks/mutations/note";
import {
    ChangeRequestOutcome,
    IChangeRequestOutcomePayload,
} from "../../types/notes.types";

interface ChangeRequestOutcomeFormProps {
    noteId: string;
    requestId?: string;
    onFormSubmit(): void;
}

const schema = yup.object({
    outcome: yup
        .string()
        .nullable()
        .required("Choose how this request was resolved"),
    comment: yup.string(),
});

const outcomeOptions = [
    {
        value: ChangeRequestOutcome.CLINICIAN_CORRECTED,
        label: "The clinician corrected the note",
    },
    {
        value: ChangeRequestOutcome.FLAGGED_IN_ERROR,
        label: "Flagged in error - no change was needed",
    },
];

export default function ChangeRequestOutcomeForm({
    noteId,
    requestId,
    onFormSubmit,
}: ChangeRequestOutcomeFormProps) {
    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<IChangeRequestOutcomePayload>({
        resolver: yupResolver(schema),
        mode: "onChange",
    });

    const params = useParams();
    const clientId = params.clientId as string;

    const { toast } = useToast();
    const queryClient = useQueryClient();

    const resolveChange = useResolveNoteChange(noteId);
    const updateOutcome = useUpdateChangeRequestOutcome(
        noteId,
        requestId || ""
    );

    const onSubmit = (data: IChangeRequestOutcomePayload) => {
        const mutation = requestId ? updateOutcome : resolveChange;
        mutation.mutate(
            {
                outcome: data.outcome,
                comment: data.comment?.trim() || undefined,
            },
            {
                onSuccess: async (res) => {
                    await queryClient.invalidateQueries({
                        queryKey: [`notes`],
                    });
                    await queryClient.invalidateQueries({
                        queryKey: ["supervisee"],
                    });
                    await queryClient.invalidateQueries({
                        queryKey: [noteId, `change-request`],
                    });
                    await queryClient.invalidateQueries({
                        queryKey: [clientId, `session-note`, noteId],
                    });

                    toast({
                        mode: "success",
                        message:
                            res.message ||
                            (requestId
                                ? "Change request outcome updated"
                                : "Change request resolved"),
                    });
                    onFormSubmit();
                },
                onError: (err) => {
                    toast({
                        mode: "error",
                        message:
                            err.response?.data.message ||
                            (requestId
                                ? "Could not update the outcome at this time"
                                : "Could not resolve the change at this time"),
                    });
                },
            }
        );
    };

    return (
        <form id="change-request-outcome" onSubmit={handleSubmit(onSubmit)}>
            <div className="fg flex flex-col gap-y-1">
                <p className="font-medium !text-caption text-textSec flex items-center gap-x-1">
                    How was this request resolved?
                </p>
                <div className="flex flex-col gap-y-[--spacing-items]">
                    {outcomeOptions.map((option) => (
                        <RadioInput
                            key={option.value}
                            {...register("outcome")}
                            label={option.label}
                            value={option.value}
                        />
                    ))}
                </div>
                <span className="text-caption flex items-center gap-x-1 text-[--color-info-text] font-medium">
                    <InfoIcon className="size-3" />
                    Requests flagged in error don&apos;t count against the
                    clinician&apos;s late, first-pass or correction metrics.
                </span>
                {errors.outcome ? (
                    <span className="!text-danger text-[11px] flex items-center gap-x-1">
                        <XCircleIcon className="size-3" />
                        {errors.outcome.message}
                    </span>
                ) : null}
            </div>
            <div className="fg">
                <TextArea {...register("comment")} label="Comment (optional)" />
            </div>
        </form>
    );
}
